/**
 * Billing_Service.ts
 * Bill generation (delegates to SK Billing via the adapter).
 */

import { apiClient } from '../../api/axiosInstance';
import { Hosp_Order_Bill_Url, Hosp_Order_Issue_Url, Hosp_Order_Settle_Url } from '../../api/endpoints';
import { newIdempotencyKey } from '../../lib/idempotency';
import type { BillResponse } from '../../schemas/billing/billing';

export const generateBill = async (
  orderId: string,
  idempotencyKey: string = newIdempotencyKey('bill'),
): Promise<BillResponse> => {
  const { data } = await apiClient.post<BillResponse>(Hosp_Order_Bill_Url(orderId), { idempotencyKey });
  return data;
};

/**
 * "Send Bill" — actually issues the real document (DRAFT → ISSUED), not just
 * a re-read. STABLE key, same reasoning as settleOrder below: a double-click
 * must resolve to the SAME issue attempt, not silently no-op-then-diverge.
 */
export const issueBill = async (
  orderId: string,
  idempotencyKey: string = `issue:${orderId}`,
): Promise<BillResponse> => {
  const { data } = await apiClient.post<BillResponse>(Hosp_Order_Issue_Url(orderId), { idempotencyKey });
  return data;
};

export const settleOrder = async (
  orderId: string,
  // STABLE key, not a random one: settling is a real payment — a double-click
  // or network retry must return the SAME payment, never create a second one.
  // Billing's own idempotency dedup only works if retries of the SAME intent
  // send the SAME key; a random default (like generateBill above uses) would
  // defeat that protection for something this financially sensitive.
  idempotencyKey: string = `settle:${orderId}`,
  // The table's linked queue entry — lets the adapter resolve the real payer
  // contact from it instead of depending on a fresh CRM call.
  entryId?: string,
): Promise<any> => {
  const { data } = await apiClient.post(Hosp_Order_Settle_Url(orderId), { idempotencyKey, entryId });
  return data;
};
